import { useCallback, useMemo, useState } from "react";
import { saveAs } from "file-saver";
import { DocumentReference, getDoc } from "firebase/firestore";
import { useCurrentTenant } from "./useCurrentTenant";
import { useBranchForUser } from "./useBranchForUser";
import { useFirestore } from "./useFirestore.web";
import { getBackendURL } from "../environment";

const filenameMapping = {
  appointment: "Terminbrief.docx",
  retour: "Retourbrief.docx",
  cover: "Begleitschreiben.docx",
  projectCover: "Begleitschreiben - Auftraggeber.docx",
  finalCover: "Begleitschreiben - Schlussexamplar.docx",
};

interface Branch {
  name: string;
  street: string;
  zipCode: string;
  city: string;
}

interface Tenant {
  name: string;
  primaryColor: string;
  logoImageUrl: string;
  website: string;
  reviewUrl?: string;
}

export const useCreateLetter = (
  letterType: "appointment" | "retour" | "cover" | "projectCover" | "finalCover"
) => {
  const [isLoading, setIsLoading] = useState(false);

  const tenant = useCurrentTenant();
  const [branch] = useBranchForUser();
  const [, , doc] = useFirestore();

  const tenantMetaData = useMemo(() => {
    const { name, primaryColor, logoImageUrl, website, reviewUrl } =
      tenant?.data() || {};

    return {
      name: name ?? "",
      primaryColor: primaryColor ?? "",
      logoImageUrl: logoImageUrl ?? "",
      website: website ?? "",
      reviewUrl: reviewUrl || undefined,
    } as Tenant;
  }, [tenant]);

  const branchMetaData = useMemo(() => {
    const { name, street, zipCode, city } = branch?.data() || {};

    return {
      name: name ?? "",
      street: street ?? "",
      zipCode: zipCode ?? "",
      city: city ?? "",
    } as Branch;
  }, [branch]);

  const createLetter = useCallback(
    async (payload: any, authorUserId?: string) => {
      setIsLoading(true);

      let authorOverride: any;
      let branchOverride: Branch | undefined;

      if (authorUserId) {
        try {
          const userSnap = await getDoc(
            doc(["users", authorUserId].join("/"))
          );
          const userData = (userSnap.data() as any) ?? {};

          let city = "";
          const branchRef = userData.branchRef as DocumentReference | undefined;
          if (branchRef) {
            const branchSnap = await getDoc(branchRef);
            const b = (branchSnap.data() as any) ?? {};
            branchOverride = {
              name: b.name ?? "",
              street: b.street ?? "",
              zipCode: b.zipCode ?? "",
              city: b.city ?? "",
            };
            city = branchOverride.city;
          }

          authorOverride = {
            name: userData.name ?? "",
            eMail: userData.eMail ?? "",
            phone: userData.phone ?? "",
            address: userData.protocolAuthor ?? "",
            signatureImageUrl: userData.signatureImageUrl ?? "",
            city,
          };
        } catch (e) {
          console.error("Failed to resolve letter author from user id", e);
        }
      }

      const body = {
        tenant: tenantMetaData,
        branch: branchMetaData,
        ...payload,
        ...(authorOverride ? { author: authorOverride } : {}),
        ...(branchOverride ? { branch: branchOverride } : {}),
      };

      const response = await fetch(`${getBackendURL()}/letter/${letterType}`, {
        method: "POST",
        headers: {
          Accept: "application/octet-stream",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const blob = await response.blob();

      saveAs(blob, filenameMapping[letterType]);
      setIsLoading(false);
    },
    [setIsLoading, tenantMetaData, branchMetaData, letterType, doc]
  );

  const isReady = !!tenant && !!branch;

  return [createLetter, isLoading, isReady] as const;
};
